/**
 * Product Grid – interactive behaviour
 * Handles: color swatch switching, size selection, quick add-to-cart.
 */

import { ajaxAddToCart } from '../../woocommerce/cart-ajax.js';

export function initProductGrid( root = document ) {
	const cards = [ ...root.querySelectorAll( '.product-card' ) ].filter( ( card ) => ! card.dataset.pgInit );
	if ( ! cards.length ) return;

	if ( ! ( 'IntersectionObserver' in window ) ) {
		cards.forEach( initCard );
		return;
	}

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( ! entry.isIntersecting ) return;
				observer.unobserve( entry.target );
				initCard( entry.target );
			} );
		},
		{ rootMargin: '500px 0px' }
	);

	cards.forEach( ( card ) => observer.observe( card ) );
}

const productGridConfig = window.dbProductGrid || {};
const messages          = productGridConfig.i18n || {};

function initCard( card ) {
	if ( card.dataset.pgInit ) return;
	card.dataset.pgInit = '1';

	const raw = card.dataset.product;
	if ( ! raw ) return;

	let data;
	try {
		data = JSON.parse( raw );
	} catch {
		return;
	}

	const mainImg = card.querySelector( '.product-card__image--main' );
	const addBtn  = card.querySelector( '.product-card__add-btn' );
	const trigger = card.querySelector( '.product-card__quick-trigger' );
	const sizesEl = card.querySelector( '.product-card__sizes' );

	// Seed selected color from first active swatch
	const firstSwatch = card.querySelector( '.product-card__swatch.is-active' );
	if ( firstSwatch ) {
		card.dataset.selectedColor = firstSwatch.dataset.color;
	}

	// ── Color swatches ────────────────────────────────────────────────────────

	card.querySelectorAll( '.product-card__swatch' ).forEach( ( swatch ) => {
		swatch.addEventListener( 'click', () => {
			card.querySelectorAll( '.product-card__swatch' ).forEach( ( s ) =>
				s.classList.remove( 'is-active' )
			);
			swatch.classList.add( 'is-active' );

			const color = swatch.dataset.color;
			card.dataset.selectedColor = color;

			// Swap the main product image to this variation's image
			if ( mainImg && swatch.dataset.full ) {
				mainImg.src = swatch.dataset.full;
				mainImg.removeAttribute( 'srcset' );
				mainImg.removeAttribute( 'sizes' );
			}

			// Refresh size availability for the new color
			updateSizeAvailability( card, data, color );
		} );
	} );

	// ── Size buttons ──────────────────────────────────────────────────────────

	card.querySelectorAll( '.product-card__size-btn' ).forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			if ( btn.classList.contains( 'is-unavailable' ) ) return;

			card.querySelectorAll( '.product-card__size-btn' ).forEach( ( b ) =>
				b.classList.remove( 'is-selected' )
			);
			btn.classList.add( 'is-selected' );
			card.dataset.selectedSize = btn.dataset.size;

			// Update add button label once a size is chosen
			if ( addBtn ) {
				setButtonLabel( addBtn, messages.addToCart );
			}
		} );
	} );

	// ── Mobile touch trigger ──────────────────────────────────────────────────

	trigger?.addEventListener( 'click', ( e ) => {
		e.stopPropagation();
		card.classList.toggle( 'is-quick-open' );
	} );

	document.addEventListener( 'click', ( e ) => {
		if ( ! card.contains( e.target ) ) {
			card.classList.remove( 'is-quick-open' );
		}
	} );

	// ── Add to cart ───────────────────────────────────────────────────────────

	addBtn?.addEventListener( 'click', async () => {
		if ( addBtn.disabled ) return;

		const hasSizes     = data.sizes && data.sizes.length > 0;
		const selectedSize = card.dataset.selectedSize || '';

		if ( hasSizes && ! selectedSize ) {
			shake( sizesEl );
			return;
		}

		if ( data.type === 'variable' ) {
			const selectedColor = card.dataset.selectedColor || '';
			const variation     = findVariation( data, selectedColor, selectedSize );

			if ( ! variation ) {
				shake( addBtn );
				return;
			}

			const attrs = {};
			if ( data.colorAttr && selectedColor ) {
				attrs[ `attribute_${ data.colorAttr }` ] = selectedColor;
			}
			if ( data.sizeAttr && selectedSize ) {
				attrs[ `attribute_${ data.sizeAttr }` ] = selectedSize;
			}

			await postAddToCart( addBtn, data.id, variation.id, attrs );
		} else {
			await postAddToCart( addBtn, data.id, 0, {} );
		}
	} );
}

// ── Size availability ─────────────────────────────────────────────────────────

function updateSizeAvailability( card, data, selectedColor ) {
	if ( ! data.sizeAttr || ! data.vars ) return;

	card.querySelectorAll( '.product-card__size-btn' ).forEach( ( btn ) => {
		const size      = btn.dataset.size;
		const available = data.vars.some( ( v ) => {
			const colorOk = ! selectedColor || v.c === '' || v.c === selectedColor;
			const sizeOk  = v.s === size || v.s === '';
			return colorOk && sizeOk && v.ok;
		} );

		btn.classList.toggle( 'is-unavailable', ! available );

		if ( ! available && btn.classList.contains( 'is-selected' ) ) {
			btn.classList.remove( 'is-selected' );
			delete card.dataset.selectedSize;
		}
	} );
}

// ── Find best-matching variation ──────────────────────────────────────────────

function findVariation( data, color, size ) {
	if ( ! data.vars ) return null;

	// Prefer exact match on both attributes
	return (
		data.vars.find(
			( v ) =>
				( v.c === color || v.c === '' ) &&
				( v.s === size  || v.s === '' ) &&
				v.ok
		) ||
		data.vars.find(
			( v ) =>
				( v.c === color || v.c === '' ) &&
				( v.s === size  || v.s === '' )
		) ||
		null
	);
}

// ── AJAX add to cart ──────────────────────────────────────────────────────────

async function postAddToCart( btn, productId, variationId, attrs ) {
	const origText = getButtonLabel( btn );
	btn.disabled   = true;
	btn.classList.add( 'is-loading' );
	setButtonLabel( btn, messages.loading );

	const result = await ajaxAddToCart( { productId, variationId, attributes: attrs } );

	if ( ! result.success ) {
		setBtn( btn, 'is-error', origText, result.message || messages.error );
		return;
	}

	setBtn( btn, 'is-success', origText, messages.added );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function setBtn( btn, cls, origText, msg ) {
	btn.classList.remove( 'is-loading' );
	btn.classList.add( cls );
	setButtonLabel( btn, msg );

	setTimeout( () => {
		btn.classList.remove( cls );
		setButtonLabel( btn, origText );
		btn.disabled    = false;
	}, 2500 );
}

function getButtonLabel( btn ) {
	return btn.querySelector( '.btn__label' )?.textContent || '';
}

function setButtonLabel( btn, label ) {
	const labelEl = btn.querySelector( '.btn__label' );
	if ( labelEl ) {
		labelEl.textContent = label;
	}
}

function shake( el ) {
	if ( ! el ) return;
	el.classList.remove( 'needs-selection' );
	void el.offsetWidth; // force reflow so animation restarts
	el.classList.add( 'needs-selection' );
}
